import React, { FC, useState, useMemo, useRef, useEffect } from "react";
import { Search, Filter as FilterIcon } from "lucide-react";
import "../../../styles/verifyPage.scss";
import VerifyTable, { VerifyDataType } from "./tables/VerifyTable";
import axios from "axios";
import { isAuth } from "../../auth/ProtectedRoute";
import swal from "sweetalert";


interface RequestItem {
  id: string;
  organizationName: string;
  user: {
    name: string;
    avatar: string;
  };
  proofType: string;
  submissionDate: string;
  status: "Approved" | "Pending" | "Requires More Info";
}

const VerifyPage: FC = () => {
  const [activeTab, setActiveTab] = useState<"Requests" | "Requested MoreInfo" | "Approved">("Requests");
  const [requests, setRequests] = useState<RequestItem[]>([]);
  const [moreInfoList, setMoreInfoList] = useState<RequestItem[]>([]);
  const [approvedList, setApprovedList] = useState<RequestItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  
  // Search & Filter State
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"All" | "Approved" | "Pending" | "Requires More Info">("All");
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const filterDropdownRef = useRef<HTMLDivElement>(null);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [totalRequests, setTotalRequests] = useState(0);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "N/A";
    try {
      const normalized = dateStr.replace(" ", "T");
      const d = new Date(normalized);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (e) {
      return dateStr;
    }
  };

  const fetchRequests = (page: number, status: "pending_review" | "requires_more_info" | "verified") => {
    setLoading(true);
    axios({
      method: "GET",
      url: process.env.REACT_APP_HOST_API + `organisation-verifications?status=${status}&page=${page}`,
      headers: {
        Authorization: `Bearer ${isAuth()}`,
      },
    })
      .then((res) => {
        setLoading(false);
        if (res.data && res.data.response) {
          const fetchedData = res.data.data.data.map((item: any) => {
            // Extract requesting user info
            const submittedBy = item.latest_verification_request?.submitted_by;
            const userName = submittedBy?.fullname || submittedBy?.name || (status === "verified" ? "Verified Organization" : "No Name");
            const userAvatar = submittedBy?.image || submittedBy?.profile_image || "";

            // Extract proof types
            const proofs = item.latest_verification_request?.proofs || [];
            let proofType = proofs
              .map((p: any) => {
                if (p.proof_type === "business_registration") return "Business Registration";
                if (p.proof_type === "official_email_domain") return "Official Email Domain";
                return p.proof_type ? p.proof_type.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase()) : "";
              })
              .filter(Boolean)
              .join(", ");

            if (!proofType) {
              proofType = status === "verified" ? "Verified" : "Official Email, Employment Letter, Company ID card"; // Fallback
            }

            return {
              id: item.id.toString(),
              organizationName: item.name || "N/A",
              user: {
                name: userName,
                avatar: userAvatar,
              },
              proofType: proofType,
              submissionDate: formatDate(item.verification_submitted_at || item.updated_at || item.created_at),
              status: item.verification_status === "pending_review"
                ? "Pending"
                : item.verification_status === "requires_more_info"
                  ? "Requires More Info"
                  : "Approved",
            };
          });

          if (status === "pending_review") {
            setRequests(fetchedData);
          } else if (status === "requires_more_info") {
            setMoreInfoList(fetchedData);
          } else {
            setApprovedList(fetchedData);
          }
          setTotalRequests(res.data.data.total || 0);
          setItemsPerPage(res.data.data.per_page || 15);
        }
      })
      .catch((err) => {
        console.error("Error fetching requests:", err);
        setLoading(false);
        swal({
          title: "Error!",
          text: "Something went wrong fetching organization verifications.",
          icon: "error",
        });
      });
  };

  // Trigger loading and fetch requests
  useEffect(() => {
    if (activeTab === "Requests") {
      fetchRequests(currentPage, "pending_review");
    } else if (activeTab === "Requested MoreInfo") {
      fetchRequests(currentPage, "requires_more_info");
    } else {
      fetchRequests(currentPage, "verified");
    }
  }, [activeTab, currentPage]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target as Node)) {
        setIsFilterDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const activeDataList = activeTab === "Requests" ? requests : activeTab === "Requested MoreInfo" ? moreInfoList : approvedList;
  const setActiveDataList = activeTab === "Requests" ? setRequests : activeTab === "Requested MoreInfo" ? setMoreInfoList : setApprovedList;

  // Status Handlers passed down to VerifyTable
  const handleStatusChange = (id: string, newStatus: "Approved" | "Pending" | "Requires More Info") => {
    setActiveDataList(prev =>
      prev.map(item => (item.id === id ? { ...item, status: newStatus } : item))
    );
  };

  const handleDelete = (id: string) => {
    setActiveDataList(prev => prev.filter(item => item.id !== id));
  };

  // Filtered Data (Search and Status filters)
  const processedData = useMemo(() => {
    let result = [...activeDataList];

    // Search filter
    if (searchTerm.trim() !== "") {
      const lowerSearch = searchTerm.toLowerCase();
      result = result.filter(
        item =>
          item.organizationName.toLowerCase().includes(lowerSearch) ||
          item.user.name.toLowerCase().includes(lowerSearch) ||
          item.proofType.toLowerCase().includes(lowerSearch)
      );
    }

    // Status filter
    if (statusFilter !== "All") {
      result = result.filter(item => item.status === statusFilter);
    }

    return result;
  }, [activeDataList, searchTerm, statusFilter]);

  // Map processedData to VerifyDataType for VerifyTable props
  const verifyTableData = useMemo<VerifyDataType[]>(() => {
    return processedData.map(item => ({
      key: item.id,
      id: item.id,
      organizationName: item.organizationName,
      userName: item.user.name,
      userAvatar: item.user.avatar,
      proofType: item.proofType,
      submissionDate: item.submissionDate,
      status: item.status,
    }));
  }, [processedData]);

  // Statistics calculation based on all requests & approved verifications
  const stats = useMemo(() => {
    const totalRequestsCount = requests.length + moreInfoList.length + approvedList.length;
    const approvedCount = approvedList.length;
    const rate = totalRequestsCount > 0 ? Math.round((approvedCount / totalRequestsCount) * 100) : 0;
    const pendingCount = requests.filter(i => i.status === "Pending").length;
    return {
      approvalRate: `${rate}%`,
      avgTime: "24 hours",
      approvedRequests: approvedCount.toString(),
      pendingReviews: (pendingCount + moreInfoList.length).toString()
    };
  }, [requests, moreInfoList, approvedList]);

  const totalItemsCount = useMemo(() => {
    if (searchTerm.trim() !== "" || statusFilter !== "All") {
      return processedData.length;
    }
    return totalRequests;
  }, [processedData.length, totalRequests, searchTerm, statusFilter]);

  return (
    <div className="verify-page-container">
      {/* Top Header */}
      <div className="verify-header-section">
        <h1>Review Queue</h1>
      </div>

      {/* Stats Cards */}
      <div className="stats-cards-grid">
        <div className="stat-card">
          <span className="stat-label">Approval rate</span>
          <span className="stat-value">{stats.approvalRate}</span>
          <div className="stat-bar-container">
            <div className="stat-bar" style={{ width: stats.approvalRate }} />
          </div>
        </div>
        <div className="stat-card">
          <span className="stat-label">Average review time</span>
          <span className="stat-value">{stats.avgTime}</span>
          <div className="stat-bar-container">
            <div className="stat-bar" style={{ width: "80%" }} />
          </div>
        </div>
        <div className="stat-card">
          <span className="stat-label">Approved Requests</span>
          <span className="stat-value">{stats.approvedRequests}</span>
          <div className="stat-bar-container">
            <div className="stat-bar" style={{ width: "100%" }} />
          </div>
        </div>
        <div className="stat-card">
          <span className="stat-label">Pending Reviews</span>
          <span className="stat-value">{stats.pendingReviews}</span>
          <div className="stat-bar-container">
            <div className="stat-bar" style={{ width: "50%" }} />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <button
          className={`tab-btn ${activeTab === "Requests" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("Requests");
            setCurrentPage(1);
            setSearchTerm("");
            setStatusFilter("All");
          }}
        >
          Requests
        </button>
        <button
          className={`tab-btn ${activeTab === "Requested MoreInfo" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("Requested MoreInfo");
            setCurrentPage(1);
            setSearchTerm("");
            setStatusFilter("All");
          }}
        >
          Requested MoreInfo
        </button>
        <button
          className={`tab-btn ${activeTab === "Approved" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("Approved");
            setCurrentPage(1);
            setSearchTerm("");
            setStatusFilter("All");
          }}
        >
          Approved
        </button>
      </div>

      {/* Actions Toolbar */}
      <div className="toolbar-container">
        <div className="search-box">
          <Search className="search-icon" size={18} />
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={e => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="filter-dropdown-wrapper" ref={filterDropdownRef}>
          <button
            className={`filter-btn ${statusFilter !== "All" ? "active-filter" : ""}`}
            onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
          >
            <FilterIcon size={16} />
            <span>Filter</span>
          </button>
          
          {isFilterDropdownOpen && (
            <div className="filter-dropdown-menu">
              <div className="filter-dropdown-header">Filter by Status</div>
              <button
                className={`filter-option ${statusFilter === "All" ? "selected" : ""}`}
                onClick={() => {
                  setStatusFilter("All");
                  setIsFilterDropdownOpen(false);
                }}
              >
                All Statuses
              </button>
              <button
                className={`filter-option ${statusFilter === "Approved" ? "selected" : ""}`}
                onClick={() => {
                  setStatusFilter("Approved");
                  setIsFilterDropdownOpen(false);
                }}
              >
                Approved
              </button>
              <button
                className={`filter-option ${statusFilter === "Pending" ? "selected" : ""}`}
                onClick={() => {
                  setStatusFilter("Pending");
                  setIsFilterDropdownOpen(false);
                }}
              >
                Pending
              </button>
              <button
                className={`filter-option ${statusFilter === "Requires More Info" ? "selected" : ""}`}
                onClick={() => {
                  setStatusFilter("Requires More Info");
                  setIsFilterDropdownOpen(false);
                }}
              >
                Requires More Info
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div className="project-table">
        <VerifyTable
          loading={loading}
          data={verifyTableData}
          onStatusChange={handleStatusChange}
          onDelete={handleDelete}
          currentPage={currentPage}
          pageSize={itemsPerPage}
          total={totalItemsCount}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>
    </div>
  );
};

export default VerifyPage;
